import React, { useEffect, useMemo, useRef, useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import { useMutation } from '@tanstack/react-query';
import { intakeChatService } from '../../services/intakeChatService';
import { useLessonStore } from '../../store/lessonStore';
import type { ChatMessage, ChatOption, IntakeSummary } from '../../types';

interface Phase0ChatProps {
  lessonId: string;
  initialHistory?: ChatMessage[];
  initialSummary?: IntakeSummary;
  onApprove: (summary: IntakeSummary) => Promise<void>;
  readOnly?: boolean;
}

interface ChatMsg {
  kind: 'chat';
  role: 'user' | 'assistant';
  content: string;
  options?: ChatOption[];
}

type DisplayMessage = ChatMsg;

const CEFR_COLORS: Record<string, string> = {
  A1: '#4caf50', A2: '#8bc34a',
  B1: '#2196f3', B2: '#1565c0',
  C1: '#9c27b0', C2: '#6a1b9a',
};

export const Phase0Chat: React.FC<Phase0ChatProps> = ({
  lessonId,
  initialHistory = [],
  initialSummary,
  onApprove,
  readOnly = false,
}) => {
  const { currentLesson, currentVersion } = useLessonStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<DisplayMessage[]>(() =>
    initialHistory.map((m) => ({
      kind: 'chat' as const,
      role: m.role as 'user' | 'assistant',
      content: m.content,
    }))
  );
  const [history, setHistory] = useState<ChatMessage[]>(() => initialHistory);
  const [input, setInput] = useState('');
  const [currentSummary, setCurrentSummary] = useState<IntakeSummary | null>(
    () => initialSummary ?? null
  );
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [initSent, setInitSent] = useState(() => initialHistory.length > 0 || readOnly);

  // Index of the last assistant message — approve button is anchored here.
  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return i;
    }
    return -1;
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const mutation = useMutation({
    mutationFn: ({
      message,
      currentHistory,
    }: {
      message: string;
      currentHistory: ChatMessage[];
    }) =>
      intakeChatService.sendMessage({
        lesson_id: lessonId,
        version: currentVersion?.version_number ?? 1,
        message,
        history: currentHistory,
      }),
    onSuccess: (data, variables) => {
      const isInit = variables.message === '__init__';
      setMessages((prev) => {
        const next: DisplayMessage[] = [...prev];
        if (!isInit) {
          next.push({ kind: 'chat', role: 'user', content: variables.message });
        }
        next.push({ kind: 'chat', role: 'assistant', content: data.reply, options: data.options });
        return next;
      });
      if (isInit) {
        setHistory([{ role: 'assistant', content: data.reply }]);
      } else {
        setHistory((prev) => [
          ...prev,
          { role: 'user', content: variables.message },
          { role: 'assistant', content: data.reply },
        ]);
      }
      if (data.summary) {
        setCurrentSummary(data.summary);
      }
    },
  });

   
  useEffect(() => {
    if (!initSent) {
      setInitSent(true);
      mutation.mutate({ message: '__init__', currentHistory: [] });
    }
  }, []);

  const sendMessage = (msg: string) => {
    if (!msg || mutation.isPending || readOnly) return;
    setInput('');
    mutation.mutate({ message: msg, currentHistory: history });
  };

  const handleSend = () => sendMessage(input.trim());

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleApprove = async () => {
    if (!currentSummary) return;
    setApproving(true);
    setApproveError('');
    try {
      await onApprove(currentSummary);
    } catch {
      setApproveError('Failed to save. Please try again.');
      setApproving(false);
    }
  };

  const showInitSpinner = initSent && messages.length === 0 && mutation.isPending;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>

      {/* ── Header ── */}
      <Box sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AutoAwesomeIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Lesson Planning</Typography>
            <Typography variant="caption" color="text.secondary">
              Design your lesson with the AI — activities, timing, vocabulary, and approach
            </Typography>
          </Box>
        </Box>
        {currentLesson && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={currentLesson.cefr_level} size="small"
              sx={{ bgcolor: CEFR_COLORS[currentLesson.cefr_level] ?? 'grey.500', color: 'white', fontWeight: 700, fontSize: '0.7rem' }}
            />
            {currentLesson.topic && <Chip label={currentLesson.topic} size="small" variant="outlined" />}
            <Chip label={`${currentLesson.duration_min} min`} size="small" variant="outlined" />
          </Box>
        )}
      </Box>

      {/* ── Chat thread ── */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {showInitSpinner && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Paper elevation={0} sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderRadius: '18px 18px 18px 4px' }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <CircularProgress size={12} />
                <Typography variant="caption" color="text.secondary">Starting lesson planning…</Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {messages.map((msg, i) => {
          const isLastAssistant = i === lastAssistantIndex;
          const hasOptions = msg.role === 'assistant' && isLastAssistant && !!msg.options?.length;
          const showApprove = msg.role === 'assistant' && isLastAssistant && !!currentSummary && !readOnly;

          return (
            <Box
              key={i}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 0.75 }}
            >
              {/* Message bubble */}
              <Box sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 1, alignItems: 'flex-start', width: '100%' }}>
                {msg.role === 'assistant' && (
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                )}
                <Paper
                  elevation={0}
                  sx={{
                    px: 2, py: 1.5, maxWidth: '76%',
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'action.hover',
                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  }}
                >
                  {msg.role === 'user' ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                      {msg.content}
                    </Typography>
                  ) : (
                    // Render AI markdown — override wmde-markdown defaults so the
                    // background is transparent and text colour inherits from the bubble.
                    <Box
                      data-color-mode="light"
                      sx={{
                        fontSize: '14px',
                        lineHeight: 1.65,
                        '& .wmde-markdown': {
                          background: 'transparent',
                          fontFamily: 'inherit',
                          fontSize: 'inherit',
                          color: 'inherit',
                          lineHeight: 'inherit',
                        },
                        '& .wmde-markdown p': { mb: 0.75, mt: 0 },
                        '& .wmde-markdown p:last-child': { mb: 0 },
                        '& .wmde-markdown ul, & .wmde-markdown ol': { pl: 2.5, mb: 0.75, mt: 0 },
                        '& .wmde-markdown li': { mb: 0.25 },
                        '& .wmde-markdown strong': { fontWeight: 700 },
                        '& .wmde-markdown table': {
                          borderCollapse: 'collapse',
                          width: '100%',
                          mb: 0.75,
                          fontSize: '13px',
                        },
                        '& .wmde-markdown th': {
                          bgcolor: 'rgba(0,0,0,0.08)',
                          fontWeight: 700,
                          px: 1,
                          py: 0.5,
                          border: '1px solid rgba(0,0,0,0.15)',
                          textAlign: 'left',
                        },
                        '& .wmde-markdown td': {
                          px: 1,
                          py: 0.5,
                          border: '1px solid rgba(0,0,0,0.15)',
                          verticalAlign: 'top',
                        },
                        '& .wmde-markdown h1, & .wmde-markdown h2, & .wmde-markdown h3': {
                          fontSize: '15px',
                          fontWeight: 700,
                          mt: 1,
                          mb: 0.5,
                          pb: 0.25,
                          borderBottom: '1px solid rgba(0,0,0,0.1)',
                        },
                        '& .wmde-markdown hr': { my: 1, borderColor: 'rgba(0,0,0,0.12)' },
                        '& .wmde-markdown code': {
                          bgcolor: 'rgba(0,0,0,0.06)',
                          px: 0.5,
                          borderRadius: '3px',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                        },
                        '& .wmde-markdown blockquote': {
                          borderLeft: '3px solid rgba(0,0,0,0.2)',
                          pl: 1.5,
                          ml: 0,
                          color: 'rgba(0,0,0,0.6)',
                          fontStyle: 'italic',
                        },
                      }}
                    >
                      <MDEditor.Markdown source={msg.content} />
                    </Box>
                  )}
                </Paper>
                {msg.role === 'user' && (
                  <Avatar sx={{ bgcolor: 'grey.400', width: 32, height: 32 }}>
                    <PersonIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                )}
              </Box>

              {/* Option buttons — last assistant message only */}
              {hasOptions && (
                <Box sx={{ pl: '44px', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {msg.options!.map((opt) => (
                    <Button
                      key={opt.label}
                      size="small"
                      variant="outlined"
                      onClick={() => sendMessage(opt.label)}
                      disabled={mutation.isPending}
                      sx={{ borderRadius: 4, fontSize: '12px', textTransform: 'none', py: 0.25 }}
                    >
                      <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>{opt.label}</Box>
                      — {opt.summary}
                    </Button>
                  ))}
                </Box>
              )}

              {/* Approve action — inline below last AI message when summary is ready */}
              {showApprove && (
                <Box sx={{ pl: '44px', display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                  {/* Metadata chips summarising what was agreed */}
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip
                      label={currentSummary!.cefr_level} size="small"
                      sx={{ bgcolor: CEFR_COLORS[currentSummary!.cefr_level] ?? 'grey.500', color: 'white', fontWeight: 700, fontSize: '0.68rem' }}
                    />
                    <Chip label={`${currentSummary!.duration_min} min`} size="small" variant="outlined" />
                    {currentSummary!.stages && currentSummary!.stages.length > 0 && (
                      <Chip label={`${currentSummary!.stages.length} stages`} size="small" variant="outlined" />
                    )}
                    {currentSummary!.language_focus?.grammar?.[0] && (
                      <Chip label={currentSummary!.language_focus.grammar[0]} size="small" variant="outlined" />
                    )}
                  </Box>

                  {approveError && (
                    <Alert severity="error" sx={{ py: 0, borderRadius: 1 }}>{approveError}</Alert>
                  )}

                  <Button
                    variant="contained"
                    color="success"
                    endIcon={approving ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
                    onClick={handleApprove}
                    disabled={approving}
                    sx={{ alignSelf: 'flex-start', borderRadius: 3, px: 3 }}
                  >
                    {approving ? 'Saving…' : 'Approve & Generate Lesson Plan'}
                  </Button>
                  <Typography variant="caption" color="text.disabled">
                    Not quite right? Keep chatting to refine.
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}

        {/* Thinking indicator */}
        {mutation.isPending && !showInitSpinner && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Paper elevation={0} sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderRadius: '18px 18px 18px 4px' }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <CircularProgress size={12} />
                <Typography variant="caption" color="text.secondary">Thinking…</Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {mutation.isError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>Something went wrong. Please try again.</Alert>
        )}

        {/* Restored approve button when returning with a saved summary but no messages yet */}
        {messages.length > 0 && lastAssistantIndex === -1 && currentSummary && !readOnly && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Button
              variant="contained" color="success"
              endIcon={approving ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
              onClick={handleApprove}
              disabled={approving}
              sx={{ alignSelf: 'flex-start', borderRadius: 3, px: 3 }}
            >
              {approving ? 'Saving…' : 'Approve & Generate Lesson Plan'}
            </Button>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* ── Input ── */}
      {!readOnly ? (
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth multiline maxRows={4}
            placeholder={
              currentSummary
                ? 'Continue refining, or approve the plan above…'
                : mutation.isPending ? 'Please wait…' : 'Reply to the AI or continue planning your lesson…'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={mutation.isPending}
            size="small"
            inputRef={inputRef}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!input.trim() || mutation.isPending}
            aria-label="send"
            sx={{ minWidth: 48, height: 40, borderRadius: 3 }}
          >
            <SendIcon fontSize="small" />
          </Button>
        </Box>
      ) : (
        <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary">
            Lesson planning complete (read-only — lesson generation has begun)
          </Typography>
        </Box>
      )}
    </Box>
  );
};
